'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useCalendarStore } from '@/stores/calendar-store';
import type { ThemeStyle } from '@/stores/calendar-store';

const THEME_KEY = 'vacant-day-theme-style';

const COLOR_THEMES: { id: ThemeStyle; label: string; description: string; swatch: string }[] = [
  { id: 'sunlit-atlas', label: 'Sunlit Atlas', description: 'Warm parchment', swatch: '#f6efe3' },
  { id: 'signal-ledger', label: 'Signal Ledger', description: 'Cool blue-grey', swatch: '#edf3f8' },
  { id: 'ember-glow', label: 'Ember Glow', description: 'Warm dark', swatch: '#1a1410' },
  { id: 'neon-drift', label: 'Neon Drift', description: 'Dark vibrant', swatch: '#0d1117' },
];

export function NavThemePicker({ lang }: { lang?: string }) {
  const [open, setOpen] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<ThemeStyle>('sunlit-atlas');
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Read theme from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(THEME_KEY) as ThemeStyle | null;
    if (saved && COLOR_THEMES.some(t => t.id === saved)) {
      setCurrentTheme(saved);
    }
  }, []);

  // Close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  // Arrow key navigation within dropdown
  const handleDropdownKeyDown = useCallback((e: React.KeyboardEvent) => {
    const items = menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]');
    if (!items || items.length === 0) return;
    const currentIndex = Array.from(items).findIndex(el => el === document.activeElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
      items[next].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
      items[prev].focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1].focus();
    }
  }, []);

  const selectTheme = useCallback((theme: ThemeStyle) => {
    setCurrentTheme(theme);
    document.documentElement.setAttribute('data-style', theme);
    try { localStorage.setItem(THEME_KEY, theme); } catch (_) { /* quota exceeded — theme applies visually but won't persist */ }
    // Sync to Zustand store (safe even on non-calendar pages)
    useCalendarStore.getState().setThemeStyle(theme);
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        aria-label="Theme"
        aria-haspopup="true"
        aria-expanded={open}
        className="rounded-lg px-2.5 py-1.5 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="13.5" cy="6.5" r="2.5" />
          <circle cx="19" cy="13" r="2.5" />
          <circle cx="6" cy="12" r="2.5" />
          <circle cx="14" cy="19" r="2.5" />
          <path d="M2 12a10 10 0 1 0 18-6" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 min-w-[200px] content-card p-1 shadow-lg animate-in fade-in slide-in-from-top-1 duration-150"
          onKeyDown={handleDropdownKeyDown}
        >
          <div className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wider text-text-tertiary">
            Theme
          </div>
          {COLOR_THEMES.map((theme) => (
            <button
              key={theme.id}
              role="menuitem"
              aria-selected={currentTheme === theme.id}
              onClick={() => selectTheme(theme.id)}
              className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover ${
                currentTheme === theme.id
                  ? 'font-semibold text-text-primary'
                  : 'text-text-secondary'
              }`}
            >
              <span
                className="inline-block h-4 w-4 shrink-0 rounded-full border border-border-subtle"
                style={{ backgroundColor: theme.swatch }}
                aria-hidden="true"
              />
              <span className="flex flex-col">
                <span>{theme.label}</span>
                <span className="text-xs text-text-tertiary">{theme.description}</span>
              </span>
              {currentTheme === theme.id && (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-auto shrink-0 text-text-primary">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
